import { useEffect, useState } from 'react';
import { documentsApi, documentLabel } from '../../api/documents.js';
import { Badge } from '../ui/Badge.jsx';
import { formatDate, expiryStatus, daysUntil } from '../../utils/dates.js';
import { FileTypeIcon } from './FileTypeIcon.jsx';
import { DocumentForm } from './DocumentForm.jsx';
import { RenewForm } from './RenewForm.jsx';

// Slot-urile standard afișate ca și carduri goale dacă nu există un document activ de tipul respectiv.
const STANDARD_SLOTS = [
  { tip: 'RCA', label: 'RCA', required: true },
  { tip: 'ITP', label: 'ITP', required: true },
  { tip: 'rovinieta', label: 'Rovinietă', required: true },
  { tip: 'talon', label: 'Talon', required: true, permanent: true },
  { tip: 'carte_auto', label: 'Carte auto', required: true, permanent: true },
  { tip: 'CASCO', label: 'CASCO', required: false },
];
const STANDARD_TIPS = new Set(STANDARD_SLOTS.map((s) => s.tip));

function StatusBadge({ iso }) {
  if (!iso) return <Badge tone="ok">Permanent</Badge>;
  const status = expiryStatus(iso);
  const d = daysUntil(iso);
  if (status === 'expired') return <Badge tone="danger">Expirat {-d}z</Badge>;
  if (status === 'danger') return <Badge tone="danger">În {d}z</Badge>;
  if (status === 'warn') return <Badge tone="warn">În {d}z</Badge>;
  return <Badge tone="ok">În {d}z</Badge>;
}

function DocumentCard({ doc, onRenew, onRefresh }) {
  const status = expiryStatus(doc.data_expirare);
  const urgent = status === 'danger' || status === 'expired';

  async function handleDelete(e) {
    e.stopPropagation();
    e.preventDefault();
    if (!confirm(`Ștergi ${documentLabel(doc.tip)}? Fișierul se șterge.`)) return;
    try {
      await documentsApi.remove(doc.id);
      onRefresh?.();
    } catch (ex) {
      alert('Eroare: ' + (ex.body?.error || ex.message));
    }
  }

  function handleRenew(e) {
    e.stopPropagation();
    e.preventDefault();
    onRenew(doc);
  }

  const hasFile = Boolean(doc.fisier_path);
  const FileLink = hasFile ? 'a' : 'div';
  const linkProps = hasFile
    ? { href: documentsApi.fileUrl(doc.id), target: '_blank', rel: 'noreferrer' }
    : {};

  return (
    <FileLink
      {...linkProps}
      className={`group relative flex flex-col rounded-card border bg-white p-4 transition-all ${
        urgent ? 'border-danger/30 bg-[#FFF9F9]' : 'border-border'
      } ${hasFile ? 'cursor-pointer hover:shadow-card-hover hover:-translate-y-0.5' : ''}`}
    >
      {urgent && <div className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r bg-danger" aria-hidden />}

      {/* Header: icon file type + meniu */}
      <div className="mb-3 flex items-start justify-between">
        <FileTypeIcon kind={doc.fisier_tip} size={36} />
        <button
          onClick={handleDelete}
          title="Șterge"
          className="invisible rounded-md p-1 text-muted hover:bg-danger/10 hover:text-danger group-hover:visible focus:visible"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
        </button>
      </div>

      {/* Body: tip + date */}
      <div className="min-h-0 flex-1">
        <div className="text-sm font-semibold text-ink">{documentLabel(doc.tip)}</div>
        {doc.data_expirare && (
          <div className="mt-1 font-mono text-[11px] text-muted">
            Expiră {formatDate(doc.data_expirare)}
          </div>
        )}
        <div className="mt-2">
          <StatusBadge iso={doc.data_expirare} />
        </div>
      </div>

      {/* Footer: acțiuni */}
      <div className="mt-3 flex flex-col gap-1.5">
        {hasFile ? (
          <span className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-ink py-1.5 text-xs font-semibold text-cream group-hover:bg-teal-bright">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            Deschide
          </span>
        ) : (
          <span className="inline-flex items-center justify-center rounded-lg bg-cream py-1.5 text-xs font-medium text-muted">
            Fără fișier
          </span>
        )}
        {doc.data_expirare && (
          <button
            type="button"
            onClick={handleRenew}
            className="rounded-lg border border-coral/30 bg-coral/5 py-1.5 text-xs font-semibold text-coral hover:bg-coral/10"
          >
            Am reînnoit
          </button>
        )}
      </div>
    </FileLink>
  );
}

function EmptySlotCard({ slot, onAdd }) {
  const required = slot.required;
  return (
    <button
      type="button"
      onClick={() => onAdd(slot.tip)}
      className={`group flex flex-col items-start rounded-card border border-dashed p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-card-hover ${
        required ? 'border-danger/40 bg-[#FFF9F9]' : 'border-border bg-cream/40'
      }`}
    >
      <div className="mb-3 flex w-full items-start justify-between">
        <div className={`flex h-[36px] w-[36px] items-center justify-center rounded-lg ${required ? 'bg-danger/10 text-danger' : 'bg-border/60 text-muted'}`}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
        </div>
      </div>

      <div className="min-h-0 flex-1">
        <div className="text-sm font-semibold text-ink">{slot.label}</div>
        <div className="mt-2">
          {required ? (
            <Badge tone="danger">Lipsește</Badge>
          ) : (
            <Badge tone="muted">Opțional</Badge>
          )}
        </div>
      </div>

      <span className="mt-3 inline-flex w-full items-center justify-center rounded-lg border border-border bg-white py-1.5 text-xs font-semibold text-ink group-hover:border-coral group-hover:text-coral">
        + Adaugă
      </span>
    </button>
  );
}

function ExpiryAlert({ doc, onRenew }) {
  const status = expiryStatus(doc.data_expirare);
  const d = daysUntil(doc.data_expirare);
  const isExpired = status === 'expired';

  let message;
  if (isExpired) message = `Expirat de ${-d} ${-d === 1 ? 'zi' : 'zile'}`;
  else if (d === 0) message = 'Expiră azi';
  else if (d === 1) message = 'Expiră mâine';
  else message = `Expiră în ${d} ${d === 1 ? 'zi' : 'zile'}`;

  return (
    <div className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 ${
      isExpired || status === 'danger'
        ? 'border-danger/30 bg-[#FFF5F5]'
        : 'border-warn/30 bg-warn/5'
    }`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <FileTypeIcon kind={doc.fisier_tip} size={22} />
          <span className="font-semibold text-ink">{documentLabel(doc.tip)}</span>
        </div>
        <div className={`mt-0.5 text-xs font-medium ${isExpired || status === 'danger' ? 'text-danger' : 'text-warn'}`}>
          {message} · {formatDate(doc.data_expirare)}
        </div>
      </div>
      <button
        onClick={() => onRenew(doc)}
        className="btn bg-coral text-white text-xs hover:brightness-95 whitespace-nowrap"
      >
        Am reînnoit
      </button>
    </div>
  );
}

function ResolvedCard({ doc }) {
  return (
    <div className="flex flex-col rounded-card border border-border bg-white p-4 opacity-60">
      <FileTypeIcon kind={doc.fisier_tip} size={30} />
      <div className="mt-2 text-sm font-semibold text-muted line-through">{documentLabel(doc.tip)}</div>
      <div className="mt-1 font-mono text-[10px] text-muted">
        Rezolvat {formatDate(doc.resolved_at)}
      </div>
      {doc.fisier_path && (
        <a
          href={documentsApi.fileUrl(doc.id)}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-flex items-center justify-center rounded-lg bg-cream py-1 text-[11px] font-medium text-muted hover:bg-border"
        >
          Vezi istoric
        </a>
      )}
    </div>
  );
}

export function DocumentsTab({ carId }) {
  const [docs, setDocs] = useState(null);
  const [err, setErr] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [preTip, setPreTip] = useState(null);
  const [renewDoc, setRenewDoc] = useState(null);

  async function load() {
    try {
      const { documents } = await documentsApi.listForCar(carId);
      setDocs(documents);
    } catch (ex) {
      setErr(ex.body?.error || 'Eroare');
    }
  }

  useEffect(() => {
    load();
  }, [carId]);

  function openAdd(tip = null) {
    setPreTip(tip);
    setAddOpen(true);
  }

  const active = (docs || []).filter((d) => !d.resolved_at);
  const resolved = (docs || []).filter((d) => d.resolved_at);

  // Pentru fiecare slot standard: dacă există un doc activ de tipul respectiv, folosim cardul real;
  // altfel, afișăm un slot gol.
  const byStandard = {};
  for (const slot of STANDARD_SLOTS) byStandard[slot.tip] = null;
  const extra = [];
  for (const d of active) {
    if (STANDARD_TIPS.has(d.tip)) {
      if (!byStandard[d.tip]) byStandard[d.tip] = d;
      else extra.push(d);
    } else {
      extra.push(d);
    }
  }

  const missingRequired = STANDARD_SLOTS.filter((s) => s.required && !byStandard[s.tip]).length;

  // „Ce expiră curând" — docs active cu data_expirare în ≤30 zile sau deja expirate.
  // Sortate: cele mai urgente (expirate → danger → warn).
  const urgencyRank = { expired: 3, danger: 2, warn: 1, ok: 0 };
  const expiringSoon = active
    .filter((d) => {
      const s = expiryStatus(d.data_expirare);
      return s === 'expired' || s === 'danger' || s === 'warn';
    })
    .sort((a, b) => urgencyRank[expiryStatus(b.data_expirare)] - urgencyRank[expiryStatus(a.data_expirare)]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-wider">
          {docs && missingRequired > 0 && (
            <span className="text-danger">{missingRequired} {missingRequired === 1 ? 'document lipsă' : 'documente lipsă'}</span>
          )}
          {docs && missingRequired === 0 && active.length > 0 && (
            <span className="text-ok">Toate documentele standard sunt înregistrate</span>
          )}
          {resolved.length > 0 && <span className="text-muted/70"> · {resolved.length} în istoric</span>}
        </div>
        <button onClick={() => openAdd(null)} className="btn-primary text-sm">
          + Adaugă document
        </button>
      </div>

      {err && <div className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{err}</div>}

      {docs === null && <div className="card text-center text-muted">Se încarcă…</div>}

      {docs && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          {STANDARD_SLOTS.map((slot) => {
            const doc = byStandard[slot.tip];
            return doc ? (
              <DocumentCard key={slot.tip} doc={doc} onRenew={setRenewDoc} onRefresh={load} />
            ) : (
              <EmptySlotCard key={slot.tip} slot={slot} onAdd={openAdd} />
            );
          })}
          {extra.map((d) => (
            <DocumentCard key={d.id} doc={d} onRenew={setRenewDoc} onRefresh={load} />
          ))}
        </div>
      )}

      {expiringSoon.length > 0 && (
        <section className="space-y-2">
          <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted">
            <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-coral" />
            Ce urmează să expire ({expiringSoon.length})
          </h3>
          <div className="space-y-2">
            {expiringSoon.map((d) => (
              <ExpiryAlert key={d.id} doc={d} onRenew={setRenewDoc} />
            ))}
          </div>
        </section>
      )}

      {resolved.length > 0 && (
        <details className="mt-6">
          <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-muted hover:text-ink">
            Istoric rezolvate ({resolved.length})
          </summary>
          <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
            {resolved.map((d) => <ResolvedCard key={d.id} doc={d} />)}
          </div>
        </details>
      )}

      <DocumentForm
        open={addOpen}
        onClose={() => { setAddOpen(false); setPreTip(null); }}
        carId={carId}
        preTip={preTip}
        onCreated={load}
      />
      <RenewForm
        open={Boolean(renewDoc)}
        onClose={() => setRenewDoc(null)}
        document={renewDoc}
        onRenewed={load}
      />
    </div>
  );
}
